import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { exportCsv } from "@/lib/csv";
import { Download } from "lucide-react";

export default function MasterData() {
  const { t } = useLang();
  const [tab, setTab] = useState("weavers");
  const [weavers, setWeavers] = useState([]);
  const [looms, setLooms] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [wForm, setWForm] = useState({ name: "", phone: "", village: "", zone: "B" });

  const load = async () => {
    const [w, l, v] = await Promise.all([api.get("/weavers"), api.get("/looms"), api.get("/vendors")]);
    setWeavers(w.data); setLooms(l.data); setVendors(v.data);
  };
  useEffect(() => { load(); }, []);

  const createWeaver = async () => {
    if (!wForm.name) return;
    await api.post("/weavers", wForm);
    setWForm({ name: "", phone: "", village: "", zone: "B" });
    load();
  };

  const updateLoomZone = async (id, zone) => {
    await api.patch(`/looms/${id}`, { zone });
    load();
  };

  const doExport = () => {
    if (tab === "weavers") {
      exportCsv(weavers, [
        { key: "name", label: "Name" },
        { key: "phone", label: "Phone" },
        { key: "village", label: "Village" },
        { key: "zone", label: "Zone" },
        { key: "notes", label: "Notes" },
      ], "weavers.csv");
    } else if (tab === "looms") {
      exportCsv(looms, [
        { key: "loom_number", label: "Loom" },
        { key: "weaver_name", label: "Weaver" },
        { key: "village", label: "Village" },
        { key: "zone", label: "Zone" },
        { key: "loom_type", label: "Type" },
        { key: "status", label: "Status" },
        { key: "last_grade", label: "Last Grade" },
      ], "looms.csv");
    } else {
      exportCsv(vendors, [
        { key: "name", label: "Vendor" },
        { key: "type", label: "Type" },
        { key: "contact", label: "Contact" },
        { key: "active_lines_count", label: "Active Lines" },
      ], "vendors.csv");
    }
  };

  return (
    <div>
      <div className="eyebrow">{t("nav_master_data")}</div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>Master Data</h1>
        <button data-testid="md-export-btn" onClick={doExport} className="h-10 px-3 rounded-md border border-[#1B3A5C]/15 text-sm flex items-center gap-1">
          <Download size={14} /> {t("export_csv")}
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        {["weavers", "looms", "vendors"].map((v) => (
          <button
            key={v}
            data-testid={`md-tab-${v}`}
            onClick={() => setTab(v)}
            className={`h-10 px-3 rounded-md text-sm ${tab === v ? "bg-[#1B3A5C] text-white" : "bg-white border border-[#1B3A5C]/15"}`}
          >{t(v)}</button>
        ))}
      </div>

      {tab === "weavers" && (
        <div className="mt-4">
          <div className="hag-surface p-4 mb-3 grid grid-cols-1 md:grid-cols-5 gap-2">
            <input data-testid="w-name" placeholder="Name" value={wForm.name} onChange={(e) => setWForm({ ...wForm, name: e.target.value })} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md" />
            <input placeholder="Phone" value={wForm.phone} onChange={(e) => setWForm({ ...wForm, phone: e.target.value })} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md" />
            <input placeholder="Village" value={wForm.village} onChange={(e) => setWForm({ ...wForm, village: e.target.value })} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md" />
            <select value={wForm.zone} onChange={(e) => setWForm({ ...wForm, zone: e.target.value })} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md">
              <option>A</option><option>B</option><option>C</option>
            </select>
            <button data-testid="w-add-btn" onClick={createWeaver} className="h-10 px-4 rounded-md font-semibold text-white" style={{ background: "var(--brand-navy)" }}>Add Weaver</button>
          </div>
          <div className="overflow-x-auto hag-scroll hag-surface">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6F0] text-[#4A627A] uppercase text-xs tracking-widest">
                <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Village</th><th className="p-3 text-left">Zone</th><th className="p-3 text-left">Phone</th></tr>
              </thead>
              <tbody>
                {weavers.map((w) => <tr key={w.id} className="border-t border-[#1B3A5C]/5"><td className="p-3">{w.name}</td><td className="p-3">{w.village}</td><td className="p-3">{w.zone}</td><td className="p-3">{w.phone}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "looms" && (
        <div className="mt-4 overflow-x-auto hag-scroll hag-surface">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6F0] text-[#4A627A] uppercase text-xs tracking-widest">
              <tr><th className="p-3 text-left">Loom</th><th className="p-3 text-left">Weaver</th><th className="p-3 text-left">Village</th><th className="p-3 text-left">Zone</th><th className="p-3 text-left">Status</th></tr>
            </thead>
            <tbody>
              {looms.map((l) => (
                <tr key={l.id} className="border-t border-[#1B3A5C]/5">
                  <td className="p-3 font-semibold">{l.loom_number}</td>
                  <td className="p-3">{l.weaver_name}</td>
                  <td className="p-3">{l.village}</td>
                  <td className="p-3">
                    <select data-testid={`loom-zone-${l.loom_number}`} value={l.zone || ""} onChange={(e) => updateLoomZone(l.id, e.target.value)} className="h-8 border border-[#1B3A5C]/15 rounded px-2 text-sm">
                      <option value="">—</option>
                      <option>A</option><option>B</option><option>C</option>
                    </select>
                  </td>
                  <td className="p-3">{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "vendors" && (
        <div className="mt-4 overflow-x-auto hag-scroll hag-surface">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6F0] text-[#4A627A] uppercase text-xs tracking-widest">
              <tr><th className="p-3 text-left">Vendor</th><th className="p-3 text-left">Type</th><th className="p-3 text-left">Active Lines</th></tr>
            </thead>
            <tbody>
              {vendors.map((v) => <tr key={v.id} className="border-t border-[#1B3A5C]/5"><td className="p-3 font-semibold">{v.name}</td><td className="p-3">{v.type}</td><td className="p-3">{v.active_lines_count}</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
